/**
 * AgentActionConfirmModal (spec 014 — IBA approval queue, T118)
 *
 * Confirmation dialog for the three admin actions on an Independent Business
 * Agent: Approve, Reject, Suspend. Approval is consequential — it flips ALL of
 * the agent's accrued Locked earnings to Available in the same transaction —
 * so the dialog states that explicitly before the admin commits.
 *
 * The dialog also captures an optional multiline Reason which is recorded in
 * the agent's lifecycle audit history (passed through to the API as `{ reason }`).
 *
 * A11y pattern mirrors CancelJobModal: focus moves to the safe (cancel) button
 * on open and is restored on close, Tab is trapped inside the dialog, ESC cancels.
 */

import { useEffect, useRef, useState } from 'react';
import type { AgentAdminRow } from '../services/agentAdminService';

export type AgentAdminAction = 'approve' | 'reject' | 'suspend';

export interface AgentActionConfirmModalProps {
  isOpen: boolean;
  action: AgentAdminAction;
  agent: AgentAdminRow | null;
  onConfirm: (reason?: string) => Promise<void>;
  onCancel: () => void;
}

function usd(amount: number): string {
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

/** Minimal spinner — animation gated on motion-safe (prefers-reduced-motion) */
function Spinner() {
  return (
    <span
      role="status"
      aria-label="Loading"
      className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full motion-safe:animate-spin"
    />
  );
}

const COPY: Record<
  AgentAdminAction,
  {
    title: string;
    confirmLabel: string;
    confirmingLabel: string;
    confirmClass: string;
    calloutClass: string;
    calloutTextClass: string;
    callout: (agent: AgentAdminRow) => string;
    /** Extra hint rendered under the Reason input, when the action needs one. */
    reasonHint: string | null;
  }
> = {
  approve: {
    title: 'Approve agent?',
    confirmLabel: 'Approve Agent',
    confirmingLabel: 'Approving…',
    confirmClass: 'bg-emerald-700 hover:bg-emerald-600',
    calloutClass: 'bg-emerald-950/40 border-emerald-500/30',
    calloutTextClass: 'text-emerald-300',
    callout: (agent) =>
      `Approving unlocks this agent's accrued Locked earnings — ${usd(agent.lockedUsd)} flips to Available immediately, in the same transaction.`,
    reasonHint: null,
  },
  reject: {
    title: 'Reject application?',
    confirmLabel: 'Reject Application',
    confirmingLabel: 'Rejecting…',
    confirmClass: 'bg-red-700 hover:bg-red-600',
    calloutClass: 'bg-red-950/40 border-red-500/30',
    calloutTextClass: 'text-red-300',
    callout: (agent) =>
      `The agent will be marked Rejected. Accrued earnings (${usd(agent.lockedUsd)}) stay Locked and are never paid out.`,
    reasonHint: null,
  },
  suspend: {
    title: 'Suspend agent?',
    confirmLabel: 'Suspend Agent',
    confirmingLabel: 'Suspending…',
    confirmClass: 'bg-amber-700 hover:bg-amber-600',
    calloutClass: 'bg-amber-950/40 border-amber-500/30',
    calloutTextClass: 'text-amber-300',
    callout: () =>
      'The agent will be marked Suspended and stops earning on referrals until re-approved.',
    reasonHint:
      "The agent's referral code is deactivated — their QR/link stops working immediately.",
  },
};

export function AgentActionConfirmModal({
  isOpen,
  action,
  agent,
  onConfirm,
  onCancel,
}: AgentActionConfirmModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  const [isInFlight, setIsInFlight] = useState(false);
  const [reason, setReason] = useState('');

  // Reset the reason on every open so it never leaks across actions/agents.
  // Render-time derived-state pattern (not an effect) per React guidance.
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen);
    if (isOpen) setReason('');
  }

  // Focus the safe (cancel) button when the modal opens; restore focus on close.
  useEffect(() => {
    if (isOpen) {
      previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
      const id = setTimeout(() => {
        cancelButtonRef.current?.focus();
      }, 0);
      return () => clearTimeout(id);
    } else {
      previouslyFocusedRef.current?.focus();
      previouslyFocusedRef.current = null;
    }
  }, [isOpen]);

  // ESC to cancel + focus trap
  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (!isInFlight) onCancel();
        return;
      }

      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = Array.from(
          dialogRef.current.querySelectorAll<HTMLElement>(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
          ),
        );

        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isInFlight, onCancel]);

  if (!isOpen || !agent) return null;

  const copy = COPY[action];

  async function handleConfirm() {
    if (isInFlight) return;
    setIsInFlight(true);
    try {
      const trimmed = reason.trim();
      await onConfirm(trimmed.length > 0 ? trimmed : undefined);
    } finally {
      setIsInFlight(false);
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-40"
        aria-hidden="true"
        onClick={() => { if (!isInFlight) onCancel(); }}
      />

      {/* Dialog */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="agent-action-title"
          aria-describedby="agent-action-desc"
          className="w-full max-w-lg bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl flex flex-col gap-4 p-6"
        >
          {/* Title */}
          <h2 id="agent-action-title" className="text-sm font-semibold text-zinc-100">
            {copy.title}
          </h2>

          <div id="agent-action-desc" className="flex flex-col gap-3">
            {/* Agent meta */}
            <div className="flex gap-2 flex-wrap items-center">
              <span className="text-sm text-zinc-200 font-medium">{agent.displayName}</span>
              {agent.referralCode && (
                <span className="text-xs bg-zinc-800 border border-zinc-700 px-2 py-0.5 rounded text-zinc-400 font-mono">
                  {agent.referralCode}
                </span>
              )}
              <span className="text-xs bg-zinc-800 border border-zinc-700 px-2 py-0.5 rounded text-zinc-500 font-mono">
                SPID {agent.spid}
              </span>
            </div>

            {/* Consequence callout */}
            <div className={`rounded-lg border px-3 py-2 ${copy.calloutClass}`}>
              <p className={`text-xs ${copy.calloutTextClass}`}>{copy.callout(agent)}</p>
            </div>

            {/* Optional reason — recorded in the agent's audit history */}
            <div className="flex flex-col gap-1">
              <label htmlFor="agent-action-reason" className="text-xs text-zinc-400 font-medium">
                Reason <span className="text-zinc-600 font-normal">(optional — recorded in the audit history)</span>
              </label>
              <textarea
                id="agent-action-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={isInFlight}
                rows={3}
                placeholder="Why are you taking this action?"
                className="w-full rounded-lg bg-zinc-950 border border-zinc-700 px-3 py-2 text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-violet-500 disabled:opacity-40 resize-y"
              />
              {copy.reasonHint && (
                <p className="text-xs text-amber-400/80">{copy.reasonHint}</p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-1">
            <button
              ref={cancelButtonRef}
              type="button"
              onClick={onCancel}
              disabled={isInFlight}
              aria-label="Cancel — make no change to this agent"
              className="min-h-[48px] px-4 py-2 rounded-lg border border-zinc-600 text-zinc-300 text-sm font-medium hover:bg-zinc-800 hover:text-zinc-100 disabled:opacity-40 transition-colors"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={() => void handleConfirm()}
              disabled={isInFlight}
              aria-label={`${copy.confirmLabel}: ${agent.displayName}`}
              className={`min-h-[48px] px-5 py-2 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium flex items-center gap-2 transition-colors ${copy.confirmClass}`}
            >
              {isInFlight ? (
                <>
                  <Spinner />
                  {copy.confirmingLabel}
                </>
              ) : (
                copy.confirmLabel
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
